<template>
  <b-overlay
      :show="isBusy"
      rounded="sm"
      opacity="0.65"
  >
    <div class="tip-settings-page">
      <b-card class="settings-header mb-2">
        <div class="header-content">
          <div>
            <h1 class="settings-title">
              Tip Distribution Settings
            </h1>

            <p class="settings-description">
              Configure how client tips are distributed between the admin and booster.
            </p>
          </div>
        </div>
      </b-card>

      <b-alert
          v-if="loadError"
          show
          variant="danger"
          class="mb-2 error-alert"
      >
        {{ loadError }}
      </b-alert>

      <b-card
          v-if="tipConfig"
          class="setting-card"
      >
        <div class="section-header">
          <div>
            <h3 class="section-title">
              Tip Distribution
            </h3>

            <p class="section-description">
              Set the percentage of each tip that belongs to the admin and booster.
              The total distribution must always equal 100%.
            </p>
          </div>

          <div
              class="total-badge"
              :class="isDistributionValid ? 'total-badge-valid' : 'total-badge-invalid'"
          >
            Total: {{ totalPercent }}%
          </div>
        </div>

        <b-alert
            show
            :variant="isDistributionValid ? 'success' : 'warning'"
            class="policy-alert"
        >
          <div class="policy-content">
            <feather-icon
                :icon="isDistributionValid ? 'CheckCircleIcon' : 'AlertTriangleIcon'"
                size="20"
                class="policy-icon"
            />

            <div>
              <strong>
                {{
                  isDistributionValid
                      ? 'Distribution is valid'
                      : 'Distribution policy'
                }}
              </strong>

              <div class="mt-25">
                Admin Tip Percentage and Booster Tip Percentage must total exactly
                <strong>100%</strong>.
              </div>

              <div
                  v-if="!isDistributionValid"
                  class="mt-50 font-weight-bold"
              >
                {{ distributionDifferenceText }}
              </div>
            </div>
          </div>
        </b-alert>

        <div
            v-if="!isDistributionValid"
            class="auto-balance-box mb-2"
        >
          <div class="auto-balance-content">
            <div>
              <strong class="auto-balance-title">
                Total must be 100%
              </strong>

              <p class="auto-balance-description">
                Automatically adjust the booster percentage based on the current admin percentage.
              </p>
            </div>

            <b-button
                variant="outline-primary"
                class="auto-balance-button"
                :disabled="isSaving"
                @click="autoBalance"
            >
              <feather-icon
                  icon="RefreshCwIcon"
                  size="16"
                  class="mr-50"
              />

              Auto Balance to 100%
            </b-button>
          </div>
        </div>

        <div class="settings-grid">
          <div class="setting-box">
            <div class="setting-top">
              <div>
                <span class="setting-label">
                  Admin Tip Percentage
                </span>

                <span class="setting-subtitle">
                  Platform share from each client tip
                </span>
              </div>

              <div class="percentage-input-wrapper">
                <b-form-input
                    v-model.number="adminPercent"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    class="percentage-input"
                    :disabled="isSaving"
                    @blur="clampAdminPercent"
                />

                <span class="percentage-symbol">
                  %
                </span>
              </div>
            </div>

            <b-form-input
                v-model.number="adminPercent"
                type="range"
                min="0"
                max="100"
                step="1"
                class="setting-range"
                :disabled="isSaving"
                :style="getRangeStyle(adminPercent)"
            />

            <div class="range-labels">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>

            <div class="decimal-value">
              API value:
              <strong>{{ adminDecimalValue.toFixed(2) }}</strong>
            </div>
          </div>

          <div class="setting-box">
            <div class="setting-top">
              <div>
                <span class="setting-label">
                  Booster Tip Percentage
                </span>

                <span class="setting-subtitle">
                  Booster share from each client tip
                </span>
              </div>

              <div class="percentage-input-wrapper">
                <b-form-input
                    v-model.number="boosterPercent"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    class="percentage-input"
                    :disabled="isSaving"
                    @blur="clampBoosterPercent"
                />

                <span class="percentage-symbol">
                  %
                </span>
              </div>
            </div>

            <b-form-input
                v-model.number="boosterPercent"
                type="range"
                min="0"
                max="100"
                step="1"
                class="setting-range"
                :disabled="isSaving"
                :style="getRangeStyle(boosterPercent)"
            />

            <div class="range-labels">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>

            <div class="decimal-value">
              API value:
              <strong>{{ boosterDecimalValue.toFixed(2) }}</strong>
            </div>
          </div>
        </div>

        <div class="distribution-summary">
          <div class="summary-item">
            <div class="summary-info">
              <div class="summary-icon admin-icon">
                <feather-icon
                    icon="ShieldIcon"
                    size="17"
                />
              </div>

              <span>
                Admin Tip Percentage
              </span>
            </div>

            <strong>
              {{ normalizedAdminPercent }}%
            </strong>
          </div>

          <div class="summary-item">
            <div class="summary-info">
              <div class="summary-icon booster-icon">
                <feather-icon
                    icon="UserIcon"
                    size="17"
                />
              </div>

              <span>
                Booster Tip Percentage
              </span>
            </div>

            <strong>
              {{ normalizedBoosterPercent }}%
            </strong>
          </div>

          <div
              class="summary-item summary-total"
              :class="{ 'summary-total-invalid': !isDistributionValid }"
          >
            <div class="summary-info">
              <div class="summary-icon total-icon">
                <feather-icon
                    icon="PieChartIcon"
                    size="17"
                />
              </div>

              <span>
                Total Distribution
              </span>
            </div>

            <strong>
              {{ totalPercent }}%
            </strong>
          </div>
        </div>

        <div
            v-if="!isDistributionValid"
            class="invalid-total-message"
        >
          <feather-icon
              icon="AlertCircleIcon"
              size="17"
          />

          <span>
            Save is disabled until Admin and Booster percentages total exactly 100%.
          </span>
        </div>

        <b-button
            variant="primary"
            block
            class="save-button"
            :disabled="!canSave"
            @click="updateTipPercent"
        >
          <b-spinner
              v-if="isSaving"
              small
              class="mr-50"
          />

          {{ isSaving ? 'Saving...' : 'Save Tip Distribution' }}
        </b-button>
      </b-card>

      <b-card
          v-else-if="!isBusy && !loadError"
          class="setting-card empty-state"
      >
        <div class="empty-state-icon">
          <feather-icon
              icon="SettingsIcon"
              size="28"
          />
        </div>

        <h4>
          No tip configuration found
        </h4>

        <p>
          Tip distribution configuration is currently unavailable.
        </p>
      </b-card>
    </div>
  </b-overlay>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BFormInput,
  BOverlay,
  BSpinner,
} from 'bootstrap-vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  GetTipsConfig,
  UpdateTipPercent,
} from '@/libs/Api/Tips'

export default {
  name: 'TipConfig',

  components: {
    BAlert,
    BButton,
    BCard,
    BFormInput,
    BOverlay,
    BSpinner,
  },

  data() {
    return {
      pending: 0,
      isSaving: false,
      loadError: '',
      tipConfig: null,
      adminPercent: 0,
      boosterPercent: 0,
      originalAdminPercent: 0,
      originalBoosterPercent: 0,
    }
  },

  computed: {
    isBusy() {
      return this.pending > 0
    },

    normalizedAdminPercent() {
      return this.toPercentage(this.adminPercent)
    },

    normalizedBoosterPercent() {
      return this.toPercentage(this.boosterPercent)
    },

    totalPercent() {
      return this.normalizedAdminPercent + this.normalizedBoosterPercent
    },

    isDistributionValid() {
      return this.totalPercent === 100
    },

    distributionDifferenceText() {
      if (this.totalPercent > 100) {
        return `The total is ${this.totalPercent - 100}% over the allowed value.`
      }

      return `${100 - this.totalPercent}% is still unallocated.`
    },

    adminDecimalValue() {
      return Number((this.normalizedAdminPercent / 100).toFixed(2))
    },

    boosterDecimalValue() {
      return Number((this.normalizedBoosterPercent / 100).toFixed(2))
    },

    hasChanges() {
      return (
          this.normalizedAdminPercent !== this.originalAdminPercent
          || this.normalizedBoosterPercent !== this.originalBoosterPercent
      )
    },

    canSave() {
      return (
          this.isDistributionValid
          && this.hasChanges
          && !this.isSaving
          && !this.isBusy
      )
    },
  },

  async created() {
    await this.loadTipConfig()
  },

  methods: {
    inc() {
      this.pending += 1
    },

    dec() {
      this.pending = Math.max(0, this.pending - 1)
    },

    executeRequest(request) {
      return new Promise((resolve, reject) => {
        request.fetch(resolve, reject)
      })
    },

    async loadTipConfig() {
      this.inc()
      this.loadError = ''

      try {
        const request = new GetTipsConfig(this)

        request.setParams({})

        const content = await this.executeRequest(request)

        const config = this.normalizeConfig(content)

        if (!config || typeof config !== 'object') {
          throw new Error('No tip configuration found.')
        }

        this.tipConfig = config

        const adminPercent = this.extractAdminPercent(config)
        const boosterPercent = this.extractBoosterPercent(
            config,
            adminPercent,
        )

        this.adminPercent = adminPercent
        this.boosterPercent = boosterPercent

        this.originalAdminPercent = adminPercent
        this.originalBoosterPercent = boosterPercent
      } catch (error) {
        this.tipConfig = null

        this.loadError = this.getErrorMessage(
            error,
            'Failed to load tip configuration.',
        )

        this.showToast(
            'error',
            this.loadError,
        )
      } finally {
        this.dec()
      }
    },

    async updateTipPercent() {
      if (!this.canSave) {
        return
      }

      this.clampAdminPercent()
      this.clampBoosterPercent()

      if (!this.isDistributionValid) {
        return
      }

      this.inc()
      this.isSaving = true

      try {
        const request = new UpdateTipPercent(this)

        request.setParams({
          adminPercent: this.adminDecimalValue,
          boosterPercent: this.boosterDecimalValue,
        })

        const content = await this.executeRequest(request)

        if (content?.isSuccess === false) {
          throw new Error(
              content?.message || 'Tip distribution could not be updated.',
          )
        }

        this.originalAdminPercent = this.normalizedAdminPercent
        this.originalBoosterPercent = this.normalizedBoosterPercent

        if (this.tipConfig) {
          this.tipConfig.adminTipPercent = this.adminDecimalValue
          this.tipConfig.boosterTipPercent = this.boosterDecimalValue
        }

        this.showToast(
            'success',
            'Tip distribution updated successfully.',
        )
      } catch (error) {
        this.showToast(
            'error',
            this.getErrorMessage(
                error,
                'Failed to update tip distribution.',
            ),
        )
      } finally {
        this.isSaving = false
        this.dec()
      }
    },

    normalizeConfig(payload) {
      return (
          payload?.data?.data
          ?? payload?.data
          ?? payload
          ?? null
      )
    },

    extractAdminPercent(config) {
      const value = (
          config?.adminTipPercent
          ?? config?.AdminTipPercent
          ?? config?.adminPercent
          ?? config?.AdminPercent
          ?? config?.tipAdminPercent
          ?? config?.TipAdminPercent
          ?? 0
      )

      return this.fromApiPercentage(value)
    },

    extractBoosterPercent(config, adminPercent) {
      const value = (
          config?.boosterTipPercent
          ?? config?.BoosterTipPercent
          ?? config?.boosterPercent
          ?? config?.BoosterPercent
          ?? config?.tipBoosterPercent
          ?? config?.TipBoosterPercent
      )

      if (
          value === undefined
          || value === null
          || value === ''
      ) {
        return Math.max(
            0,
            100 - adminPercent,
        )
      }

      return this.fromApiPercentage(value)
    },

    fromApiPercentage(value) {
      const number = Number(value)

      if (!Number.isFinite(number)) {
        return 0
      }

      const normalized = (
          number >= 0
          && number <= 1
      )
          ? number * 100
          : number

      return this.toPercentage(normalized)
    },

    toPercentage(value) {
      const number = Number(value)

      if (!Number.isFinite(number)) {
        return 0
      }

      return Math.min(
          100,
          Math.max(
              0,
              Math.round(number),
          ),
      )
    },

    clampAdminPercent() {
      this.adminPercent = this.toPercentage(
          this.adminPercent,
      )
    },

    clampBoosterPercent() {
      this.boosterPercent = this.toPercentage(
          this.boosterPercent,
      )
    },

    autoBalance() {
      if (this.isSaving) {
        return
      }

      this.clampAdminPercent()

      this.boosterPercent = Math.max(
          0,
          100 - this.normalizedAdminPercent,
      )
    },

    getRangeStyle(value) {
      return {
        '--range-progress': `${this.toPercentage(value)}%`,
      }
    },

    getErrorMessage(
        error,
        fallback = 'An unexpected error occurred.',
    ) {
      return (
          error?.response?.data?.message
          || error?.response?.data?.Message
          || error?.data?.message
          || error?.message
          || fallback
      )
    },

    showToast(type, text) {
      const isSuccess = type === 'success'

      this.$toast({
        component: ToastificationContent,
        position: 'bottom-center',
        props: {
          title: isSuccess ? 'Success' : 'Error',
          icon: isSuccess ? 'CheckIcon' : 'XIcon',
          variant: isSuccess ? 'success' : 'danger',
          text,
        },
      })
    },
  },
}
</script>

<style scoped>
.tip-settings-page {
  width: 100%;
  margin-top: 30px;
}

.settings-header {
  border: 1px solid #ebe9f1;
  border-radius: 16px;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.06);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.settings-title {
  margin: 0;
  color: #2c2c2c;
  font-size: 1.55rem;
  font-weight: 700;
}

.settings-description {
  margin: 0.45rem 0 0;
  color: #6e6b7b;
  font-size: 0.9rem;
  line-height: 1.6;
}

.setting-card {
  border: 1px solid #ebe9f1;
  border-radius: 16px;
  box-shadow: 0 4px 18px rgba(34, 41, 47, 0.06);
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.section-title {
  margin: 0;
  color: #2c2c2c;
  font-size: 1.2rem;
  font-weight: 700;
}

.section-description {
  max-width: 680px;
  margin: 0.4rem 0 0;
  color: #6e6b7b;
  font-size: 0.88rem;
  line-height: 1.6;
}

.total-badge {
  display: flex;
  min-width: 112px;
  min-height: 46px;
  padding: 0.6rem 0.9rem;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
}

.total-badge-valid {
  color: #28c76f;
  background: rgba(40, 199, 111, 0.1);
}

.total-badge-invalid {
  color: #ff9f43;
  background: rgba(255, 159, 67, 0.12);
}

.policy-alert {
  margin-bottom: 1rem;
  border-radius: 12px;
}

.policy-content {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
}

.policy-icon {
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.auto-balance-box {
  padding: 1rem;
  border: 1px dashed rgba(115, 103, 240, 0.35);
  border-radius: 14px;
  background: rgba(115, 103, 240, 0.04);
}

.auto-balance-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.auto-balance-title {
  display: block;
  margin-bottom: 0.25rem;
  color: #5e5873;
  font-size: 0.9rem;
}

.auto-balance-description {
  margin: 0;
  color: #82808b;
  font-size: 0.8rem;
  line-height: 1.5;
}

.auto-balance-button {
  flex-shrink: 0;
  border-radius: 10px;
  font-weight: 600;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.setting-box {
  padding: 1.25rem;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  background: #fafafd;
}

.setting-top {
  display: flex;
  min-height: 48px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.35rem;
}

.setting-label {
  display: block;
  color: #5e5873;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.4;
}

.setting-subtitle {
  display: block;
  margin-top: 0.3rem;
  color: #9a97a8;
  font-size: 0.73rem;
  line-height: 1.4;
}

.percentage-input-wrapper {
  position: relative;
  width: 88px;
  flex: 0 0 88px;
}

.percentage-input {
  height: 38px;
  padding-right: 27px;
  border-color: #d8d6de;
  border-radius: 9px;
  color: #7367f0;
  background: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  text-align: center;
}

.percentage-input:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.12);
}

.percentage-symbol {
  position: absolute;
  top: 50%;
  right: 10px;
  color: #9a97a8;
  font-size: 0.8rem;
  font-weight: 600;
  pointer-events: none;
  transform: translateY(-50%);
}

.percentage-input::-webkit-inner-spin-button,
.percentage-input::-webkit-outer-spin-button {
  margin: 0;
}

.setting-range {
  width: 100%;
  height: 20px;
  margin: 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
}

.setting-range:focus {
  outline: none;
}

.setting-range:disabled {
  cursor: not-allowed;
}

.setting-range::-webkit-slider-runnable-track {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(
      to right,
      #7367f0 0%,
      #7367f0 var(--range-progress),
      #dedbe9 var(--range-progress),
      #dedbe9 100%
  );
}

.setting-range::-webkit-slider-thumb {
  width: 21px;
  height: 21px;
  margin-top: -6.5px;
  border: 3px solid #fff;
  border-radius: 50%;
  appearance: none;
  -webkit-appearance: none;
  background: #7367f0;
  cursor: grab;
  box-shadow:
      0 2px 7px rgba(115, 103, 240, 0.4),
      0 0 0 2px rgba(115, 103, 240, 0.08);
}

.setting-range::-webkit-slider-thumb:active {
  cursor: grabbing;
}

.setting-range::-moz-range-track {
  height: 8px;
  border-radius: 999px;
  background: #dedbe9;
}

.setting-range::-moz-range-progress {
  height: 8px;
  border-radius: 999px;
  background: #7367f0;
}

.setting-range::-moz-range-thumb {
  width: 15px;
  height: 15px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #7367f0;
  cursor: grab;
  box-shadow: 0 2px 7px rgba(115, 103, 240, 0.4);
}

.range-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 0.55rem;
  color: #9a97a8;
  font-size: 0.68rem;
}

.decimal-value {
  margin-top: 0.9rem;
  padding-top: 0.8rem;
  border-top: 1px dashed #e4e2ea;
  color: #9a97a8;
  font-size: 0.72rem;
}

.decimal-value strong {
  color: #7367f0;
}

.distribution-summary {
  overflow: hidden;
  margin-bottom: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 12px;
}

.summary-item {
  display: flex;
  min-height: 52px;
  padding: 0.75rem 1rem;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid #ebe9f1;
  color: #5e5873;
  font-size: 0.84rem;
}

.summary-item:last-child {
  border-bottom: 0;
}

.summary-info {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.summary-icon {
  display: flex;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
}

.admin-icon {
  color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
}

.booster-icon {
  color: #00cfe8;
  background: rgba(0, 207, 232, 0.1);
}

.total-icon {
  color: #28c76f;
  background: rgba(40, 199, 111, 0.1);
}

.summary-item strong {
  color: #2c2c2c;
  font-size: 0.95rem;
}

.summary-total {
  background: rgba(40, 199, 111, 0.06);
  font-weight: 700;
}

.summary-total strong {
  color: #28c76f;
}

.summary-total-invalid {
  background: rgba(255, 159, 67, 0.08);
}

.summary-total-invalid strong {
  color: #ff9f43;
}

.summary-total-invalid .total-icon {
  color: #ff9f43;
  background: rgba(255, 159, 67, 0.12);
}

.invalid-total-message {
  display: flex;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  align-items: center;
  gap: 0.6rem;
  border-radius: 10px;
  color: #b86b19;
  background: rgba(255, 159, 67, 0.1);
  font-size: 0.82rem;
  font-weight: 600;
}

.save-button {
  min-height: 44px;
  border-radius: 10px;
  font-weight: 600;
}

.error-alert {
  border-radius: 12px;
}

.empty-state {
  padding: 2.5rem 1rem;
  text-align: center;
}

.empty-state-icon {
  display: flex;
  width: 58px;
  height: 58px;
  margin: 0 auto 1rem;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
}

.empty-state h4 {
  margin-bottom: 0.5rem;
  color: #2c2c2c;
  font-weight: 700;
}

.empty-state p {
  margin: 0;
  color: #82808b;
  font-size: 0.85rem;
}

@media (max-width: 991px) {
  .settings-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 575px) {
  .tip-settings-page {
    margin-top: 20px;
  }

  .settings-title {
    font-size: 1.3rem;
  }

  .settings-description {
    font-size: 0.82rem;
  }

  .section-header {
    flex-direction: column;
  }

  .total-badge {
    min-width: 0;
  }

  .auto-balance-content {
    align-items: stretch;
    flex-direction: column;
  }

  .auto-balance-button {
    width: 100%;
  }

  .setting-top {
    align-items: flex-start;
  }

  .summary-item {
    align-items: center;
  }

  .setting-box {
    padding: 1rem;
  }
}
</style>